import type { CachedLaunchpad, ChurchEvent, Launchpad } from "../model/launchpad";
import { isoFromAppleEpoch } from "../util/date";
import cached from "./cached-launchpad.json";

// A real Launchpad payload, lifted from the staging iOS app's on-disk cache
// (Application Support/SaltFoundation/Storage/cached-launchpad.json). Using
// the real thing keeps the components honest about nullable fields — one song
// in here has no artworkURL, and several events have no thumbnail,
// description, or registration link.
//
// That file is the *mobile cache* format, which differs from the API in two
// ways the models do not follow: dates are Apple reference-date seconds rather
// than ISO 8601, and optional fields are absent rather than null. Both are
// normalised here, so components only ever see the live shape.
//
// Replace this with a fetch once the API is wired up; nothing downstream
// changes.

interface CachedEvent {
  id: string;
  churchID: string;
  attributes: Record<string, unknown>;
}

function adaptEvent(event: CachedEvent): ChurchEvent {
  const a = event.attributes;
  const endsAt = typeof a.endsAt === "number" ? isoFromAppleEpoch(a.endsAt) : null;

  return {
    id: event.id,
    churchID: event.churchID,
    attributes: {
      title: a.title as string,
      description: (a.description as string | undefined) ?? null,
      location: (a.location as string | undefined) ?? null,
      thumbnailURL: (a.thumbnailURL as string | undefined) ?? null,
      registrationURL: (a.registrationURL as string | undefined) ?? null,
      startsAt: isoFromAppleEpoch(a.startsAt as number),
      endsAt,
      showTimes: Boolean(a.showTimes),
      campusID: (a.campusID as string | undefined) ?? null,
      categoryID: (a.categoryID as string | undefined) ?? null,
      waivers: (a.waivers as ChurchEvent["attributes"]["waivers"] | undefined) ?? [],
    },
  };
}

const raw = cached as unknown as {
  launchpad: Omit<Launchpad, "events"> & {
    events: CachedEvent[];
    latestService: (Launchpad["latestService"] & { attributes: { timestamp: unknown } }) | null;
  };
  cachedAt: number;
};

export const SAMPLE_LAUNCHPAD: Launchpad = {
  ...raw.launchpad,
  latestService: raw.launchpad.latestService
    ? {
        ...raw.launchpad.latestService,
        attributes: {
          ...raw.launchpad.latestService.attributes,
          timestamp: isoFromAppleEpoch(
            raw.launchpad.latestService.attributes.timestamp as unknown as number
          ),
          additionalTimestamps: [],
        },
      }
    : null,
  events: raw.launchpad.events.map(adaptEvent),
};

export const SAMPLE_CACHED_LAUNCHPAD: CachedLaunchpad = {
  launchpad: SAMPLE_LAUNCHPAD,
  cachedAt: raw.cachedAt,
};

export const SAMPLE_SERVICE = SAMPLE_LAUNCHPAD.latestService;
export const SAMPLE_SONGS = SAMPLE_SERVICE?.attributes.songs ?? [];
export const SAMPLE_PLAYLISTS = SAMPLE_LAUNCHPAD.playlists;
export const SAMPLE_EVENTS = SAMPLE_LAUNCHPAD.events;
